export interface BrowserAnalyticsClient {
  identify: (id: string, traits?: Record<string, unknown>) => Promise<void>
  page: () => void
  track: (event: string, properties?: object) => void
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics?: any
    dataLayer?: Array<Record<string, unknown>>
  }
}

const APP_NAME = 'trophyverse'
const IDENTIFY_EVENT_NAME = 'legend_identify'
const TRACK_EVENT_PREFIX = 'legend_'
const SENSITIVE_TRAIT_KEYS = new Set([
  'createdat',
  'email',
  'firstname',
  'lastname',
  'name',
  'phone',
  'userid',
])

const bufferToHex = (buffer: ArrayBuffer): string => {
  return Array.from(new Uint8Array(buffer))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

const hashId = async (value: string): Promise<string | null> => {
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof TextEncoder === 'undefined') {
    return null
  }

  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))

    return bufferToHex(digest)
  } catch (exception: unknown) {
    console.error(exception)

    return null
  }
}

const getDataLayer = (): Array<Record<string, unknown>> => {
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = []
  }

  return window.dataLayer
}

const getLocale = (): string | undefined => {
  const locale = document.documentElement?.lang?.trim()

  return locale || undefined
}

const normalizeTrackEventName = (eventName: string): string => {
  const normalized = eventName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  if (!normalized) {
    return `${TRACK_EVENT_PREFIX}event`
  }

  return normalized.startsWith(TRACK_EVENT_PREFIX)
    ? normalized
    : `${TRACK_EVENT_PREFIX}${normalized}`
}

const getTrackProperties = (properties: object = {}): Record<string, unknown> => {
  if (typeof properties !== 'object' || properties === null || Array.isArray(properties)) {
    return {}
  }

  return properties as Record<string, unknown>
}

const sanitizeTraits = async (id: string, traits: Record<string, unknown> = {}): Promise<Record<string, unknown>> => {
  const sanitized: Record<string, unknown> = {
    is_identified: true,
  }
  const userIdHash = await hashId(id)

  if (userIdHash !== null) {
    sanitized.user_id_hash = userIdHash
  }

  Object.entries(traits).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return
    }

    if (SENSITIVE_TRAIT_KEYS.has(key.toLowerCase())) {
      return
    }

    sanitized[key] = value
  })

  return sanitized
}

export const createBrowserAnalyticsClient = (app: string = APP_NAME): BrowserAnalyticsClient => ({
  identify: async (id, traits = {}) => {
    const sanitizedTraits = await sanitizeTraits(id, traits)

    getDataLayer().push({
      app,
      event: IDENTIFY_EVENT_NAME,
      traits_sanitized: sanitizedTraits,
      ...sanitizedTraits,
    })
  },
  page: () => {
    getDataLayer().push({
      app,
      event: 'page_view',
      language: getLocale(),
      page_location: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_title: document.title || undefined,
    })
  },
  track: (event, properties = {}) => {
    const eventProperties = getTrackProperties(properties)

    getDataLayer().push({
      app,
      event: normalizeTrackEventName(event),
      legend_event_name: event,
      legend_event_payload: eventProperties,
      ...eventProperties,
    })
  },
})

export const ensureBrowserAnalyticsClient = (app: string = APP_NAME): BrowserAnalyticsClient => {
  getDataLayer()

  if (!window.analytics) {
    window.analytics = createBrowserAnalyticsClient(app)
  }

  return window.analytics as BrowserAnalyticsClient
}

export const buildAnalyticsBootstrapScript = (app: string = APP_NAME): string => {
  return `
    (function () {
      var app = ${JSON.stringify(app)};
      var identifyEventName = ${JSON.stringify(IDENTIFY_EVENT_NAME)};
      var trackEventPrefix = ${JSON.stringify(TRACK_EVENT_PREFIX)};
      var sensitiveKeys = ['createdat', 'email', 'firstname', 'lastname', 'name', 'phone', 'userid'];

      function hashId(value) {
        if (!window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
          return Promise.resolve(null);
        }

        return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(value)))
          .then(function (digest) {
            return Array.from(new Uint8Array(digest))
              .map(function (byte) {
                return byte.toString(16).padStart(2, '0');
              })
              .join('');
          })
          .catch(function () {
            return null;
          });
      }

      function getDataLayer() {
        if (!Array.isArray(window.dataLayer)) {
          window.dataLayer = [];
        }

        return window.dataLayer;
      }

      function getLocale() {
        var locale = document.documentElement && document.documentElement.lang;

        return locale || undefined;
      }

      function normalizeTrackEventName(eventName) {
        var normalized = String(eventName || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');

        if (!normalized) {
          return trackEventPrefix + 'event';
        }

        return normalized.indexOf(trackEventPrefix) === 0
          ? normalized
          : trackEventPrefix + normalized;
      }

      function getTrackProperties(properties) {
        if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
          return {};
        }

        return properties;
      }

      function sanitizeTraits(id, traits) {
        var sanitized = {
          is_identified: true,
        };
        var source = traits || {};

        return hashId(id).then(function (userIdHash) {
          if (userIdHash !== null) {
            sanitized.user_id_hash = userIdHash;
          }

          Object.keys(source).forEach(function (key) {
            var value = source[key];

            if (value === undefined || value === null) {
              return;
            }

            if (sensitiveKeys.indexOf(String(key).toLowerCase()) !== -1) {
              return;
            }

            sanitized[key] = value;
          });

          return sanitized;
        });
      }

      getDataLayer();

      if (!window.analytics) {
        window.analytics = {
          identify: function (id, traits) {
            return sanitizeTraits(id, traits).then(function (sanitizedTraits) {
              var identifyPayload = {
                app: app,
                event: identifyEventName,
                traits_sanitized: sanitizedTraits,
              };

              Object.assign(identifyPayload, sanitizedTraits);
              getDataLayer().push(identifyPayload);
            });
          },
          page: function () {
            getDataLayer().push({
              app: app,
              event: 'page_view',
              language: getLocale(),
              page_location: window.location.href,
              page_path: window.location.pathname + window.location.search,
              page_title: document.title || undefined,
            });
          },
          track: function (eventName, properties) {
            var eventProperties = getTrackProperties(properties);
            var trackPayload = {
              app: app,
              event: normalizeTrackEventName(eventName),
              legend_event_name: eventName,
              legend_event_payload: eventProperties,
            };

            Object.assign(trackPayload, eventProperties);
            getDataLayer().push({
              app: trackPayload.app,
              event: trackPayload.event,
              legend_event_name: trackPayload.legend_event_name,
              legend_event_payload: trackPayload.legend_event_payload,
              ...eventProperties,
            });
          }
        };
      }
    })();
  `.trim()
}
